import React, {useRef} from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Draggable from 'react-draggable';

import {X} from 'lucide-react';
import {parseFootingExpressions, parsePdfFootingExpressions} from './util';
import PropTypes from 'prop-types';

const PaperComponent = (props) => {
  const nodeRef = useRef(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-handle"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
};

const FootingCalculationDialog = ({
  open,
  handleClose,
  tableId,
  cellExpression,
  valueExpression,
  calculatedValue,
  isPdfReview = false,
}) => {
  let isError = false;
  let calculation = {};

  try {
    calculation = isPdfReview
      ? parsePdfFootingExpressions(valueExpression)
      : parseFootingExpressions(cellExpression, valueExpression, tableId);
  } catch (error) {
    console.error('Error parsing footing expression:', error);
    isError = true;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-handle"
      hideBackdrop={true}
      sx={{
        '& .MuiPaper-root': {
          width: '255px',
          minHeight: '288px',
          borderRadius: '8px',
          boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      <Box id="draggable-dialog-handle" flexGrow={1}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          padding={2}
        >
          <Typography sx={{fontWeight: 600, fontSize: 14, color: '#043B34'}}>
            Calculation details
          </Typography>

          <IconButton
            size={'medium'}
            onClick={handleClose}
            sx={{padding: '3px'}}
          >
            <X size={20} color={'#0F1E24'} />
          </IconButton>
        </Box>

        <Divider sx={{backgroundColor: '#DADFDB'}} />

        <Box p={2}>
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              borderRadius: '4px',
              backgroundColor: '#F1F4F2',
              minHeight: 200,
            }}
          >
            {isError ? (
              <Box p={2} flex={1}>
                Unable to retrieve calculation. Please contact support.
              </Box>
            ) : (
              <>
                <Box p={2} flex={1} textAlign="right">
                  {calculation.tableParts.map((tablePart, index) => {
                    const calculatedPart = calculation.calculatedParts[index];
                    const isMatching = tablePart === calculatedPart;

                    return (
                      <Typography
                        key={index}
                        sx={{
                          color: '#043B34',
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right',
                          lineHeight: '140%',
                        }}
                      >
                        {isMatching ? (
                          tablePart
                        ) : (
                          <>
                            <span
                              style={{
                                textDecoration: 'line-through',
                                textDecorationColor: 'red',
                                textDecorationThickness: '2px',
                              }}
                            >
                              {tablePart}
                            </span>{' '}
                            {calculatedPart}
                          </>
                        )}
                      </Typography>
                    );
                  })}
                </Box>

                <Divider sx={{backgroundColor: '#043B34'}} />

                <Box textAlign="right" px={2} py={1}>
                  <Typography
                    sx={{
                      color: '#043B34',
                      fontSize: '14px',
                      fontWeight: 600,
                      textAlign: 'right',
                      lineHeight: '140%',
                    }}
                  >
                    = {calculatedValue}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

FootingCalculationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  tableId: PropTypes.string.isRequired,
  cellExpression: PropTypes.string.isRequired,
  valueExpression: PropTypes.string.isRequired,
  calculatedValue: PropTypes.string.isRequired,
  isPdfReview: PropTypes.bool,
};

FootingCalculationDialog.displayName = 'FootingCalculationDialog';

export default FootingCalculationDialog;
